import React, { useMemo, useCallback, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { getOrganizationModuleCapabilities } from '../../config/organizationCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import {
  deactivateDesignation,
  getDesignationDetails,
} from '../../services/designationService';
import type { DesignationDetails } from '../../types/designation';
import { isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { organizationErrorMessage } from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'DesignationDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string | null;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.fieldLabel, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Text style={[styles.fieldValue, { color: palette.textPrimary }]}>
        {value || 'Not listed'}
      </Text>
    </View>
  );
}

export function DesignationDetailsScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.sm,
        marginTop: spacing.md,
      },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getOrganizationModuleCapabilities(auth.permissions);
  const { designationId } = route.params;
  const [item, setItem] = useState<DesignationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const data = await getDesignationDetails(designationId);
        setItem(data);
        setError(null);
      } catch (caught) {
        setItem(null);
        setError(
          organizationErrorMessage(
            caught,
            'The requested record could not be loaded.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [designationId],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.DESIGNATION_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Designation"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading designation" />
        </AppScreen>
      ) : error && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Designation"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : item ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
            />
          }
        >
          <ScreenHeader
            title={item.designationName}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {capabilities.canUpdateDesignation &&
            isActiveConfigurationRecord(item.isActive) ? (
              <ConfigurationDeleteButton
                entityLabel="designation"
                loading={softDelete.busy}
                disabled={softDelete.busy}
                onPress={() =>
                  softDelete.confirmAndDelete({
                    entityLabel: 'designation',
                    recordName: item.designationName,
                    deleteRecord: async () => {
                      await deactivateDesignation(item);
                    },
                    onSuccess: () => navigation.goBack(),
                    errorMessage: caught =>
                      organizationErrorMessage(
                        caught,
                        'Unable to delete designation.',
                      ),
                  })
                }
              />
            ) : null}
            <Field palette={palette} label="Code" value={item.designationCode} />
            <Field palette={palette} label="Description" value={item.description} />
            <Field palette={palette} label="Department" value={item.departmentName} />
            <Field palette={palette} label="Level" value={String(item.designationLevel)} />
            <Field
              palette={palette}
              label="Managerial"
              value={item.isManagerial ? 'Yes' : 'No'}
            />
            <Field palette={palette} label="Status" value={item.statusLabel} />
          </AppCard>
          <View style={styles.actions}>
            {capabilities.canUpdateDesignation ? (
              <AppButton
                title="Edit"
                onPress={() =>
                  navigation.navigate(routes.designationEdit, {
                    designationId,
                  })
                }
              />
            ) : null}
          </View>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.md },
  field: { gap: spacing.xs },
  fieldLabel: {
    ...typography.caption,
    fontWeight: '600',
  },
  fieldValue: { ...typography.body },
  actions: { gap: spacing.sm, marginTop: spacing.lg },
});
